// apps/web/src/pages/pcp/remessas-banner.tsx — Fase 17.3 (P3-WR-01).
// Banner de sucesso da tela de Remessas (recebimento de NF e estorno). Mensagem, detalhes por item e atalhos
// "Cadastrar roteiro" moram em UM objeto: trocar o banner substitui tudo, então os atalhos de um recebimento
// nunca reaparecem sob a mensagem de outra operação (como o estorno).
import type { ReactElement } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { PERMISSAO_VER_ROTEIROS, permissoesBastam, ROTULO_CADASTRAR_ROTEIRO } from '../../lib/roteiros/cadastro-peca';

export interface AtalhoDoBanner {
  readonly chave: string;
  readonly texto: string;
  readonly href: string;
  readonly rotulo?: string;
  readonly permissao?: string;
}

export interface BannerSucessoRemessas {
  readonly mensagem: string;
  readonly detalhes: readonly string[];
  readonly atalhos: readonly AtalhoDoBanner[];
}

export const MENSAGEM_RECEBIMENTO_CONFIRMADO = 'Recebimento físico confirmado e saldo movimentado no estoque com sucesso!';
export const MENSAGEM_ESTORNO_REALIZADO = 'Estorno compensatório realizado com sucesso no ledger.';

export function bannerDoRecebimento(detalhes: readonly string[], atalhos: readonly AtalhoDoBanner[]): BannerSucessoRemessas {
  return { mensagem: MENSAGEM_RECEBIMENTO_CONFIRMADO, detalhes, atalhos };
}

/** O estorno não herda nada do recebimento anterior: sem detalhes e sem atalhos. */
export function bannerDoEstorno(): BannerSucessoRemessas {
  return { mensagem: MENSAGEM_ESTORNO_REALIZADO, detalhes: [], atalhos: [] };
}

interface BannerProps {
  readonly banner: BannerSucessoRemessas | null;
  /** Permissões da sessão; `null` enquanto carrega (fail-closed: só o texto aparece, sem link). */
  readonly permissoes: readonly string[] | null;
}

export function BannerSucessoDeRemessas({ banner, permissoes }: BannerProps): ReactElement | null {
  if (banner === null) return null;
  // /pcp/roteiros exige producao:consultar; quem confirma recebimento pode não ter (P3-WR-02).
  const podeAbrirRoteiros = permissoesBastam([PERMISSAO_VER_ROTEIROS], permissoes);
  return (
    <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm font-medium flex items-center gap-2" role="status">
      <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
      <div className="space-y-1">
        <span>{banner.mensagem}</span>
        {banner.detalhes.length > 0 && (
          <ul className="font-normal text-emerald-900 space-y-0.5 list-disc pl-4">
            {banner.detalhes.map((detalhe, indice) => (
              <li key={`${indice}-${detalhe}`}>{detalhe}</li>
            ))}
          </ul>
        )}
        {banner.atalhos.length > 0 && (
          <ul className="font-normal space-y-0.5 pl-4">
            {banner.atalhos.map((atalho) => (
              <li key={atalho.chave}>
                {(atalho.permissao ? permissoesBastam([atalho.permissao], permissoes) : podeAbrirRoteiros) ? (
                  <>
                    <a className="inline-flex min-h-11 items-center text-blue-700 underline font-medium" href={atalho.href}>{atalho.rotulo ?? ROTULO_CADASTRAR_ROTEIRO}</a>
                    <span className="text-emerald-900"> — {atalho.texto}</span>
                  </>
                ) : (
                  <span className="text-emerald-900">{atalho.rotulo ?? ROTULO_CADASTRAR_ROTEIRO}: {atalho.texto} (peça ao PCP; seu perfil não abre {atalho.permissao ? 'esta ação' : 'os roteiros'})</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
